import tagsData from '@/data/tags.json';
import { SearchResult } from '@/types/search';
import { loadWordsByPrefix } from '@/lib/data/loader';

const tagsMap = tagsData as Record<string, string[]>;

/**
 * Get all words associated with a specific tag.
 * Efficiently loads only the necessary word partition files.
 */
export async function getWordsByTag(tag: string): Promise<SearchResult[]> {
  const normalizedTag = tag.toLowerCase().trim();
  
  // Directly access the property to avoid any prototype issues
  const words = Object.prototype.hasOwnProperty.call(tagsMap, normalizedTag) 
    ? tagsMap[normalizedTag] 
    : undefined;
  
  if (!words) {
    console.warn(`Tag not found: "${tag}" (normalized: "${normalizedTag}")`);
    return [];
  }

  // Group words by prefix to minimize requests
  const wordsByPrefix = new Map<string, string[]>();
  for (const word of words) {
    if (!word || word.length === 0) continue;
    let prefix: string;
    if (word.length === 1 || word[1] === ' ') {
      prefix = word.charAt(0).toLowerCase();
    } else {
      prefix = word.substring(0, 2).toLowerCase();
    }
    
    if (!wordsByPrefix.has(prefix)) {
      wordsByPrefix.set(prefix, []);
    }
    wordsByPrefix.get(prefix)!.push(word);
  }

  const results: SearchResult[] = [];
  const promises: Promise<void>[] = [];

  for (const [prefix, targetWords] of wordsByPrefix) {
    promises.push((async () => {
      try {
        const loadedWords = await loadWordsByPrefix(prefix);
        const targetSet = new Set(targetWords);
        
        for (const loadedWord of loadedWords) {
          if (targetSet.has(loadedWord.word)) {
             results.push({
               word: loadedWord,
               score: loadedWord.frequency, // Use frequency as score
               matchPosition: 0,
               matchType: 'contains' // Tag match isn't exactly text match, but close enough
             });
          }
        }
      } catch (e) {
        console.error(`Error loading words for prefix ${prefix}`, e);
      }
    })());
  }

  await Promise.all(promises);
  
  // Sort by frequency (descending)
  results.sort((a, b) => b.word.frequency - a.word.frequency);

  return results;
}

/**
 * Get list of all available tags
 */
export function getAllTags(): string[] {
    return Object.keys(tagsMap).sort();
}
